import type { ReadonlyJSONValue } from "@rocicorp/zero";
import type { ComponentType } from "react";

export type ToolUse = {
  id: string;
  toolName: string;
  input: ReadonlyJSONValue;
  output: ReadonlyJSONValue | null;
  isError: boolean | null;
};

export type ToolResult = {
  id: string;
  output: ReadonlyJSONValue;
  isError: boolean;
};

export type ToolUseProps = {
  toolUse: ToolUse;
  result: ToolResult | null;
  createdAt: number;
  completedAt?: number | null;
  interrupted: boolean;
  expanded?: boolean;
  // Rendered as the single live line of a collapsed activity group.
  compact?: boolean;
  data: unknown;
  onProvideResult?: (
    entryId: string,
    output: unknown,
    isError: boolean
  ) => void;
};

export type PendingPromptResultOptions = {
  pauseAfter?: boolean;
  sendStagedAttachments?: boolean;
  approvedPlan?: boolean;
};

export type PendingPromptArgs = {
  toolCallId: string;
  input: ReadonlyJSONValue;
  hasStagedAttachments: boolean;
  data?: unknown;
  onResult: (
    output: unknown,
    isError: boolean,
    options?: PendingPromptResultOptions
  ) => Promise<boolean>;
};

export function resolvePromptResult(
  options: PendingPromptResultOptions | undefined,
  hasStagedAttachments: boolean
) {
  const { sendStagedAttachments, approvedPlan, ...resultOptions } =
    options ?? {};
  const sendAttachments = !!sendStagedAttachments && hasStagedAttachments;
  return {
    approvedPlan: !!approvedPlan,
    sendAttachments,
    resultOptions: sendAttachments
      ? { ...resultOptions, pauseAfter: true }
      : resultOptions,
  };
}

export type ChatTool = {
  name: string;
  Header?: ComponentType<ToolUseProps>;
  Body?: ComponentType<ToolUseProps>;
  PendingBody?: ComponentType<ToolUseProps>;
  alwaysExpand?: boolean;
  // A pending call starts expanded, then collapses once it completes.
  expandWhilePending?: boolean;
  alwaysLoadOutput?: boolean;
  // The row renders only its header and cannot be expanded.
  headerOnly?: boolean;
  // When set, clicking the tool row runs this instead of expanding a body.
  onActivate?: (props: ToolUseProps) => void;
  data?: unknown;
  PendingPrompt?: ComponentType<PendingPromptArgs>;
};

export function toolResultFailed(result: ToolResult) {
  const output = result.output;
  return (
    result.isError ||
    (typeof output === "object" &&
      output !== null &&
      "ok" in output &&
      output.ok === false)
  );
}
